import { useEffect, useRef, useState } from 'react'
import { useSession } from '../IslandRoot'

/**
 * Cmd+K command palette. MVP: navigation only.
 *
 * Listens globally for ⌘K / Ctrl+K, opens a centered floating dialog
 * over the page, fuzzy-includes against the shell's screens and views, and
 * delegates selection to legacy `window.switchTab(el)` — the same
 * shim AppRail uses, so palette / rail / direct legacy clicks all
 * stay in lockstep.
 *
 * Defers to the legacy host palette: when `window.openCmdk` exists (the
 * running app), this island ignores ⌘K so only the host palette opens.
 *
 * Scope discipline (per the minimum-viable transformation plan):
 *   - Navigation only. No entity search (contacts/accounts/deals).
 *   - No action set ("Add contact", "Mark complete"). Future.
 *   - No fuzzy scoring, no recents, no telemetry.
 *   - No keyboard shortcuts beyond ⌘K / Esc / arrows / Enter.
 *
 * MIGRATION_DEBT: same as AppRail — selecting an item delegates to
 * the legacy `window.switchTab` shim. When legacy navigation is
 * decommissioned, this calls a future router instead.
 */

// Names follow the five-screen shell (frontend-simplification Phase 3);
// ids stay the legacy tab ids.
const TARGETS = [
  { id: 'today', label: 'Today' },
  { id: 'contacts', label: 'People' },
  { id: 'accounts', label: 'Companies' },
  { id: 'pipeline', label: 'Deals' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'activity', label: 'Activity' },
  { id: 'inbox', label: 'Inbox' },
  { id: 'bookings', label: 'Calendar' },
] as const

function callLegacySwitchTab(tabId: string) {
  const tabEl = document.querySelector(`.nav-tab[data-tab="${tabId}"]`) as HTMLElement | null
  if (!tabEl) return
  const fn = (window as unknown as { switchTab?: (el: HTMLElement) => void }).switchTab
  fn?.(tabEl)
}

function hostPaletteExists(): boolean {
  return typeof (window as unknown as { openCmdk?: unknown }).openCmdk === 'function'
}

export default function CommandPalette() {
  const session = useSession()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const q = query.trim().toLowerCase()
  const items = q ? TARGETS.filter((t) => t.label.toLowerCase().includes(q)) : [...TARGETS]

  // Clamp highlight if filtered list shrinks below current index.
  useEffect(() => {
    if (highlight >= items.length) setHighlight(0)
  }, [items.length, highlight])

  // Global ⌘K / Ctrl+K listener. Only attached when authed.
  useEffect(() => {
    if (session.status !== 'authed') return
    const onKey = (e: KeyboardEvent) => {
      // The legacy host palette (`openCmdk`) is a superset of this one
      // (navigation + actions + contact/account/deal search) and binds the
      // same key. When it is present, leave ⌘K to it so exactly one opens.
      if (hostPaletteExists()) return
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setOpen((v) => !v)
        setQuery('')
        setHighlight(0)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [session.status])

  // Focus the input on open.
  useEffect(() => {
    if (!open) return
    // requestAnimationFrame so the input is mounted before we focus it.
    const id = requestAnimationFrame(() => inputRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [open])

  // Lock body scroll while the palette is open.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  if (!open || session.status !== 'authed') return null

  const select = (id: string) => {
    callLegacySwitchTab(id)
    setOpen(false)
  }

  const onListKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((i) => Math.min(i + 1, Math.max(0, items.length - 1)))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((i) => Math.max(i - 1, 0))
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const item = items[highlight]
      if (item) select(item.id)
    }
  }

  return (
    <>
      <style>{`
        .cmdk-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.45);
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding-top: 12vh;
          z-index: 200;
        }
        .cmdk-panel {
          background: var(--bg2);
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
          width: min(600px, 92vw);
          max-height: 70vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: var(--shadow);
        }
        .cmdk-input {
          padding: var(--space-3) var(--space-4);
          font-family: var(--font-ui);
          font-size: var(--text-md);
          line-height: var(--leading-md);
          background: transparent;
          color: var(--text);
          border: none;
          border-bottom: 1px solid var(--border);
          outline: none;
        }
        .cmdk-input::placeholder {
          color: var(--text-dim);
        }
        .cmdk-list {
          overflow-y: auto;
          padding: var(--space-1);
        }
        .cmdk-item {
          display: flex;
          align-items: center;
          padding: var(--space-2) var(--space-3);
          font-family: var(--font-ui);
          font-size: var(--text-base);
          line-height: var(--leading-base);
          color: var(--text-dim);
          cursor: pointer;
          border-radius: var(--radius-sm);
          user-select: none;
        }
        .cmdk-item.active {
          background: var(--bg3);
          color: var(--text);
        }
        .cmdk-empty {
          padding: var(--space-4);
          font-family: var(--font-ui);
          font-size: var(--text-base);
          color: var(--text-dim);
          text-align: center;
        }
      `}</style>
      <div
        className="cmdk-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) setOpen(false)
        }}
      >
        <div className="cmdk-panel" role="dialog" aria-modal="true" aria-label="Command palette">
          <input
            ref={inputRef}
            type="text"
            className="cmdk-input"
            placeholder="Jump to…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setHighlight(0)
            }}
            onKeyDown={onListKey}
            aria-label="Command search"
            autoComplete="off"
            spellCheck={false}
          />
          <div className="cmdk-list" role="listbox" aria-label="Targets">
            {items.length === 0 ? (
              <div className="cmdk-empty">No matches</div>
            ) : (
              items.map((item, i) => (
                <div
                  key={item.id}
                  role="option"
                  aria-selected={i === highlight}
                  className={`cmdk-item${i === highlight ? ' active' : ''}`}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => select(item.id)}
                >
                  {item.label}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  )
}
